import { ArrowDown } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollReveal";

// Numbered because onboarding really is a sequence: verification cannot start
// before the application, the calendar cannot open before verification.
const STEPS = [1, 2, 3] as const;

function StepRow({ n, t }: { n: number; t: (k: string) => string }) {
  return (
    <div className="grid items-center gap-6 sm:gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-20">
      {/* The numeral is the illustration. The time it takes sits under it
          because that is the one thing every applicant asks. */}
      <div className="flex flex-col items-center lg:items-end">
        <span
          className="font-heading font-black leading-none tracking-[-0.06em]"
          style={{ fontSize: "clamp(5.5rem, 12vw, 9rem)", color: "var(--color-ther-clay)" }}
        >
          {String(n).padStart(2, "0")}
        </span>
        <span
          className="mt-2 inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-bold tabular-nums"
          style={{ background: "oklch(46% 0.085 148 / 0.14)", color: "var(--color-ther-sage-deep)" }}
        >
          {t(`forTherapists.process.step${n}Time`)}
        </span>
      </div>

      <div className="text-center lg:text-left">
        <h3 className="font-heading text-2xl font-bold tracking-[-0.01em] text-foreground sm:text-3xl">
          {t(`forTherapists.process.step${n}Title`)}
        </h3>
        <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
          {t(`forTherapists.process.step${n}Desc`)}
        </p>
      </div>
    </div>
  );
}

export function Process() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  return (
    <section
      ref={ref}
      id="process"
      className="animate-on-scroll relative overflow-hidden py-16 sm:py-24"
      style={{ background: "oklch(100% 0 0)" }}
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            className="font-heading font-black leading-[1.05] tracking-[-0.02em] text-foreground"
            style={{ fontSize: "clamp(1.9rem, 4vw, 3rem)" }}
          >
            {t("forTherapists.process.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("forTherapists.process.subtitle")}
          </p>
        </div>

        <div className="mt-14 sm:mt-20">
          {STEPS.map((n, i) => (
            <div key={n}>
              {i > 0 && (
                <div className="my-8 flex justify-center sm:my-12" aria-hidden>
                  <span
                    className="flex size-12 items-center justify-center rounded-full"
                    style={{ background: "oklch(50% 0.13 40 / 0.1)", color: "var(--color-ther-clay)" }}
                  >
                    <ArrowDown className="size-5" weight="bold" />
                  </span>
                </div>
              )}
              <StepRow n={n} t={t} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
